import api from './api'

export interface InvoiceParams {
  search?: string
  startDate?: string
  endDate?: string
  contractId?: string
  serviceId?: string
  patientId?: string
  therapistId?: string
  payoutStatus?: boolean
  page?: number
  limit?: number
}

export interface ContractId {
  _id: string
  contract_ids: string
  patient_id: string
  therapist_id: string
  [key: string]: any
}

export interface ServiceId {
  _id: string
  name: string
  [key: string]: any
}

export interface ClaimId {
  _id: string
  claimId?: string
  sessionId?: string
  sessionIds?: string
  [key: string]: any
}

export interface Invoice {
  _id: string
  invoice_ids: string
  contract_id: ContractId | string
  contract_ids?: string
  session_id?: string
  sessionId?: string
  session_ids?: string
  patient_name?: string
  patientId?: string
  therapistId?: string
  service_id?: ServiceId | string
  service_name?: string
  amount: number
  payment_date?: string
  payment_method?: string
  payment_for?: string
  claim_id?: ClaimId | string
  status: 'Paid' | 'Pending' | 'Draft' | 'Overdue'
  isPayoutCreated?: boolean
  is_deleted: boolean
  invoice_url?: string | null
  createdAt?: string
  updatedAt?: string
  [key: string]: any // Allow additional fields
}

export interface InvoicesResponse {
  invoices?: Invoice[]
  data?: Invoice[]
  totalRecords?: number
  currentPage?: number
  totalPages?: number
  totalInvoiceAmount?: number
  totalPaidAmount?: number
  totalPendingAmount?: number
  totalProviderPayout?: number
  // Legacy field names for backward compatibility
  totalInvoicedAmount?: number
  totalPendingPayment?: number
}

export const getInvoices = async (
  params: InvoiceParams
): Promise<InvoicesResponse> => {
  const response = await api.get<InvoicesResponse>('/invoices', {
    params: {
      search: params.search || '',
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      contractId: params.contractId || '',
      serviceId: params.serviceId || '',
      patientId: params.patientId || undefined,
      therapistId: params.therapistId || undefined,
      payoutStatus:
        params.payoutStatus === undefined ? undefined : params.payoutStatus,
      page: params.page || 1,
      limit: params.limit || 10,
    },
  })

  // Handle different response structures
  const data = response.data
  if (Array.isArray(data)) {
    return { invoices: data, data }
  }
  if (data.invoices) {
    return data
  }
  if (data.data) {
    return { invoices: data.data, data: data.data }
  }
  return { invoices: [], data: [] }
}

export interface ContractDropdownOption {
  _id: string
  contract_ids: string
}

export interface ServiceDropdownOption {
  _id: string
  name: string
}

export const getContractDropdowns = async (
  search?: string
): Promise<ContractDropdownOption[]> => {
  const params: Record<string, any> = {}
  if (search?.trim()) {
    params.search = search.trim()
  }
  const response = await api.get<ContractDropdownOption[]>(
    '/contracts/dropdown/contracts',
    { params }
  )
  return Array.isArray(response.data) ? response.data : []
}

export const getServiceDropdowns = async (): Promise<
  ServiceDropdownOption[]
> => {
  const response = await api.get<ServiceDropdownOption[]>(
    '/contracts/dropdown/services'
  )
  return Array.isArray(response.data) ? response.data : []
}

export const exportInvoicesCSV = async (
  params: Omit<InvoiceParams, 'page' | 'limit'>
): Promise<Blob> => {
  // Try export endpoint first, fallback to regular endpoint if not available
  try {
    const response = await api.get<Blob>('/invoices/export', {
      params: {
        search: params.search || '',
        startDate: params.startDate || '',
        endDate: params.endDate || '',
        contractId: params.contractId || '',
        serviceId: params.serviceId || '',
        patientId: params.patientId || undefined,
        therapistId: params.therapistId || undefined,
      },
      responseType: 'blob',
    })
    return response.data
  } catch (error: any) {
    // If export endpoint doesn't exist, throw error with helpful message
    if (error?.response?.status === 404) {
      throw new Error('Export functionality is not available yet')
    }
    throw error
  }
}

